/**
 * Material UI chat renderer.
 * Developers: Adrian Engelmann, Kiran Jayaramu, Nicu Bratescu, Daniela Popescu
 * Copyright 2026 by Qalgo GmbH (www.qalgo.de). All rights reserved.
 */
import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
	Box,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	IconButton,
	CircularProgress,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	Typography
} from '@mui/material';
import dayjs from 'dayjs';
import { MuiAudioInput } from './MuiAudioInput';
import { MuiFileInput } from './MuiFileInput';
import { MuiMessage } from './MuiMessage';
import { MuiMultiSelectInput } from './MuiMultiSelectInput';
import { MuiSelectInput } from './MuiSelectInput';
import { MuiTextInput } from './MuiTextInput';
import { QeeColorButtonPrimary, QeeColorButtonSecondary } from '../../../../qeeColorButton';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import GridOnOutlinedIcon from '@mui/icons-material/GridOnOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import { resolveChatUiLabels } from './chatUiLabels';
import { findGuruExportSource } from '../../../../guruExportSource';
import QeeTooltip from '../../../../qeeTooltip';
import GuruChatActionMenuItems from '../../../../guruChatHistory/GuruChatActionMenuItems';

export function MuiChat({
	chatController,
	height='90vh',
	onSupportRequest,
	supportLanguage='',
	supportUserEmail='',
	enableHumanSupport=false,
	supportNeedsEmail=false,
	onResetChat,
	onLoadGuruPdfs,
	onDownloadGuruPdf,
	onLoadGuruExcels,
	onDownloadGuruExcel,
	onNewChat,
	enableChatReset=false,
	enableLeylaBoss=true,
	leylaBossCooldown=3,
	assistantAvatarLabels={},
	chatUiLabels={},
	pendingStatus='',
	enableGuruPdfs=false,
	enableGuruExcels=false,
	onOpenChatHistory,
	showChatHistoryAction=false,
	activeGuruChat,
	guruChatActionsDisabled=false,
	onRenameGuruChat,
	onTogglePinGuruChat,
	onDeleteGuruChat
}) {
	const chatCtl = chatController;

	const [messages, setMessages] = useState(chatCtl.getMessages());
	const [actReq, setActReq] = useState(chatCtl.getActionRequest());
	const msgRef = useRef(null);
	const [showQuickQuestions, setShowQuickQuestions] = useState(false);
	const [actReqQuickQuestions, setActReqQuickQuestions] = useState(null);
	const [humanSupportRequest, setHumanSupportRequest] = useState({id: 0, message: null});
	const [resetDialogOpen, setResetDialogOpen] = useState(false);
	const [isResettingChat, setIsResettingChat] = useState(false);
	const [resetError, setResetError] = useState('');
	const [menuAnchorEl, setMenuAnchorEl] = useState(null);
	const [filesMenuAnchorEl, setFilesMenuAnchorEl] = useState(null);
	const [guruFiles, setGuruFiles] = useState([]);
	const [guruFilesLoading, setGuruFilesLoading] = useState(false);
	const [guruFilesError, setGuruFilesError] = useState('');

	const scroll = useCallback(() => {
		if (msgRef.current) {
		  msgRef.current.scrollTop = msgRef.current.scrollHeight;
		}
	}, [msgRef]);

	useEffect(() => {
		function handleMassagesChanged() {
		  setMessages([...chatCtl.getMessages()]);
		  scroll();
		}

		function handleActionChanged() {
		  setActReq(chatCtl.getActionRequest());
		  scroll();
		}

		chatCtl.addOnMessagesChanged(handleMassagesChanged);
		chatCtl.addOnActionChanged(handleActionChanged);
	}, [chatCtl, scroll]);
	
	useEffect(() => {
		const frame = window.requestAnimationFrame(scroll);
		return () => window.cancelAnimationFrame(frame);
	}, [messages, pendingStatus, actReq, showQuickQuestions, scroll]);	

	const convertQAtoOptions = useCallback((qaArray) => {
		console.log('--> convertQAtoOptions', qaArray);
		var arrRet = qaArray.map((item, index) => ({
			value: index.toString(), // Convert index to string to use as value
			text: item.question, // Use the question as the option text
			prompt: item.prompt || item.question, // Send the precise prompt while displaying the short label
			answerType: item.answerType, // Retain the original answerType
			answer: item.answer, // Retain the original answer
			quickQuestions: item.quickQuestions, // Retain the original quickQuestions
			followUpContext: item.followUpContext
		}));
		console.log('<-- convertQAtoOptions', arrRet);
		return arrRet;
	}, []); 
  
  
	useEffect(() => {
		if (actReq && actReq.quickQuestions && actReq.quickQuestions.length > 0) {
			setShowQuickQuestions(true);
			// Create a new 'fake' action request for the quick questions
			const selectActReq = {
				type: 'select',
				always: true, 
				self: true,	
				addMessage: true,	
				options: convertQAtoOptions(actReq.quickQuestions),
			};
			setActReqQuickQuestions(selectActReq);
		} else {
			setShowQuickQuestions(false);
			setActReqQuickQuestions(null);
		}
	//}, [actReq, convertQAtoOptions]);
	}, [actReq]);
  
	const CustomComponent = useMemo(() => {
		if (!actReq || actReq.type !== 'custom') {
			return null;
		}	

		return actReq.Component;
	}, [actReq]);

	const unknownMsg = {
		type: 'text',
		content: 'Unknown message.',
		self: false
	};

	let prevDate = dayjs(0);
	let prevTime = dayjs(0);
	let latestAssistantIndex = -1;
	let hasUserMessage = false;
	messages.forEach((message, index) => {
		if (!message.self && !message.deletedAt) latestAssistantIndex = index;
		if (message.self && !message.deletedAt) hasUserMessage = true;
	});
	const showResetChat = enableChatReset && hasUserMessage;
	const labels = resolveChatUiLabels(supportLanguage, chatUiLabels);
	const widgetVersion = typeof window !== 'undefined' && window.QEE_WIDGET_VERSION
		? String(window.QEE_WIDGET_VERSION)
		: '';
	const openHumanSupport = () => {
		setMenuAnchorEl(null);
		setHumanSupportRequest(value => ({
			id: value.id + 1,
			message: messages[latestAssistantIndex]
		}));
		window.setTimeout(scroll, 0);
	};
	const requestGuruPdf = async () => {
		setMenuAnchorEl(null);
		const exportSource = findGuruExportSource(messages);
		if (!exportSource) return;
		await chatCtl.addMessage({
			type: 'text',
			content: labels.saveAsPdfPrompt,
			self: true,
			pdfExportSource: exportSource
		});
	};
	const requestMyGuruFiles = async () => {
		const anchor = menuAnchorEl;
		setMenuAnchorEl(null);
		setFilesMenuAnchorEl(anchor);
		setGuruFiles([]);
		setGuruFilesError('');
		setGuruFilesLoading(true);
		try {
			const requests = [];
			if (enableGuruPdfs && typeof onLoadGuruPdfs === 'function')
				requests.push(Promise.resolve(onLoadGuruPdfs()).then(items =>
					(Array.isArray(items) ? items : []).map(item => ({...item, fileType: 'pdf'}))));
			if (enableGuruExcels && typeof onLoadGuruExcels === 'function')
				requests.push(Promise.resolve(onLoadGuruExcels()).then(items =>
					(Array.isArray(items) ? items : []).map(item => ({...item, fileType: 'excel'}))));
			if (!requests.length) throw new Error('Saved file list is unavailable.');
			const results = await Promise.allSettled(requests);
			const files = results.filter(result => result.status === 'fulfilled')
				.flatMap(result => result.value)
				.sort((left, right) => String(right.createdAt || '').localeCompare(String(left.createdAt || '')));
			if (!files.length && results.every(result => result.status === 'rejected'))
				throw results[0].reason || new Error('Saved file list is unavailable.');
			setGuruFiles(files);
		} catch (error) {
			console.error('Loading private Guru files failed:', error);
			setGuruFilesError(labels.filesLoadError);
		} finally {
			setGuruFilesLoading(false);
		}
	};
	const requestGuruExcel = async () => {
		setMenuAnchorEl(null);
		const exportSource = findGuruExportSource(messages);
		if (!exportSource) return;
		await chatCtl.addMessage({type: 'text', content: labels.saveAsExcelPrompt, self: true,
			excelExportSource: exportSource});
	};
	const openGuruFile = (item) => {
		const target = String(item && item.sdocUrl || '');
		if (!/^https?:\/\//i.test(target)) return;
		setFilesMenuAnchorEl(null);
		let preparedTarget = target;
		try {
			const targetUrl = new URL(target, window.location.href);
			if (targetUrl.origin === window.location.origin && targetUrl.pathname.toLowerCase().indexOf('qeeleaembed.html') === -1) {
				targetUrl.searchParams.set('synapcusGuruTarget', '1'); preparedTarget = targetUrl.toString();
			}
		} catch (error) { return; }
		const opened = window.open(preparedTarget, '_blank', 'noopener,noreferrer');
		if (opened) opened.opener = null;
	};
	const formatGuruPdfDate = (value) => {
		const date = dayjs(value);
		return date.isValid() ? date.format('DD.MM.YYYY HH:mm') : '';
	};
	const guruPdfFilenameHasTimestamp = (value) => {
		const filename = String(value || '');
		return /\b\d{2}\.\d{2}\.\d{4}\s+(?:um|la)\s+\d{2}-\d{2}-\d{2}(?:\s+Uhr)?\b/i.test(filename) ||
			/\b\d{4}-\d{2}-\d{2}\s+at\s+\d{2}-\d{2}-\d{2}\b/i.test(filename);
	};
	const confirmResetChat = async () => {
		if (typeof onResetChat !== 'function' || isResettingChat) return;
		setIsResettingChat(true);
		setResetError('');
		try {
			await onResetChat();
			setResetDialogOpen(false);
		} catch (error) {
			console.error('Reset chat failed:', error);
			setResetError(labels.resetError);
		} finally {
			setIsResettingChat(false);
		}
	};

	return (
		<Box
			sx={{
				height: height,
				width: '100%',
				p: 0,
				bgcolor: 'background.default',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
				minWidth: 0,
				overflow: 'hidden'
			}}
		>
			<QeeTooltip title={labels.menu}>
				<IconButton
					aria-label={labels.menu}
					aria-controls={menuAnchorEl ? 'qee-chat-options-menu' : undefined}
					aria-haspopup="true"
					aria-expanded={menuAnchorEl ? 'true' : undefined}
					onClick={(event) => setMenuAnchorEl(event.currentTarget)}
					sx={{
						position: 'absolute',
						top: 5,
						right: 5,
						zIndex: 20,
						width: 36,
						height: 36,
						bgcolor: 'rgba(255,255,255,0.92)',
						color: 'text.primary',
						boxShadow: 1,
						'&:hover': { bgcolor: 'background.paper' }
					}}
				>
					<MoreHorizIcon />
				</IconButton>
			</QeeTooltip>
			<Menu
				id="qee-chat-options-menu"
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={() => setMenuAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				MenuListProps={{
					dense: true,
					sx: { py: 0.5 }
				}}
				PaperProps={{
					sx: {
						mt: 0.5,
						width: 270,
						maxWidth: 'calc(100vw - 16px)',
						maxHeight: 'min(70vh, 520px)',
						borderRadius: 2,
						border: '1px solid',
						borderColor: 'divider',
						bgcolor: 'background.paper',
						boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
						overflowY: 'auto',
						'& .MuiMenuItem-root': {
							minHeight: 38,
							px: 1.25,
							py: 0.5,
							mx: 0.5,
							borderRadius: 1.25,
							bgcolor: 'transparent',
							color: 'text.primary',
							textTransform: 'none !important',
							fontWeight: 400,
							'&:hover': { bgcolor: 'action.hover' }
						},
						'& .MuiListItemIcon-root': {
							minWidth: 32,
							color: 'text.secondary'
						},
						'& .MuiMenuItem-root.guru-chat-delete-action': {
							color: 'error.main'
						},
						'& .guru-chat-delete-action .MuiListItemIcon-root': {
							color: 'error.main'
						},
						'& .MuiListItemText-primary': {
							fontSize: '1rem',
							lineHeight: 1.3,
							fontWeight: 400,
							textTransform: 'none !important'
						},
						'& .MuiDivider-root': { my: 0.5 },
						'@media (max-width:599.95px) and (hover:none) and (pointer:coarse)': {
							width: 290,
							'& .MuiMenuItem-root': { minHeight: 44 },
							'& .MuiListItemText-primary, & .MuiListItemText-secondary': {
								fontSize: '1rem'
							}
						}
					}
				}}
			>
				{activeGuruChat && (
					<>
						<GuruChatActionMenuItems
							chat={activeGuruChat}
							labels={labels}
							disabled={guruChatActionsDisabled}
							onRename={onRenameGuruChat}
							onTogglePin={onTogglePinGuruChat}
							onDelete={onDeleteGuruChat}
							onAction={() => setMenuAnchorEl(null)}
						/>
						<Divider />
					</>
				)}
				{showChatHistoryAction && typeof onNewChat === 'function' && (
					<MenuItem onClick={async () => {
						setMenuAnchorEl(null);
						try {
							await onNewChat();
						} catch (error) {
							console.error('Starting a new Guru chat failed:', error);
						}
					}}>
						<ListItemIcon><BorderColorOutlinedIcon fontSize="small" /></ListItemIcon>
						<ListItemText>{labels.newChat}</ListItemText>
					</MenuItem>
				)}
				{showChatHistoryAction && typeof onOpenChatHistory === 'function' && (
					<MenuItem onClick={() => {
						setMenuAnchorEl(null);
						onOpenChatHistory();
					}}>
						<ListItemIcon><ForumOutlinedIcon fontSize="small" /></ListItemIcon>
						<ListItemText>{labels.chatHistory}</ListItemText>
					</MenuItem>
				)}
				{showChatHistoryAction && (enableGuruPdfs || enableGuruExcels || enableHumanSupport || showResetChat) && <Divider />}
				{enableGuruPdfs && latestAssistantIndex >= 0 && (
					<MenuItem onClick={requestGuruPdf}>
						<ListItemIcon><PictureAsPdfOutlinedIcon fontSize="small" /></ListItemIcon>
						<ListItemText>{labels.saveAsPdf}</ListItemText>
					</MenuItem>
				)}
				{enableGuruExcels && latestAssistantIndex >= 0 && (
					<MenuItem onClick={requestGuruExcel}>
						<ListItemIcon><GridOnOutlinedIcon fontSize="small" sx={{color: '#107c41'}} /></ListItemIcon>
						<ListItemText>{labels.saveAsExcel}</ListItemText>
					</MenuItem>
				)}
				{(enableGuruPdfs || enableGuruExcels) && (
					<MenuItem onClick={requestMyGuruFiles}>
						<ListItemIcon><FolderOutlinedIcon fontSize="small" sx={{color: 'var(--primary-color, #004c3f)'}} /></ListItemIcon>
						<ListItemText>{labels.savedFiles}</ListItemText>
					</MenuItem>
				)}
				{(enableGuruPdfs || enableGuruExcels) && (enableHumanSupport || showResetChat) && <Divider />}
				{enableHumanSupport && latestAssistantIndex >= 0 && (
					<MenuItem onClick={openHumanSupport}>
						<ListItemIcon><SupportAgentIcon fontSize="small" /></ListItemIcon>
						<ListItemText>{labels.contactSupport}</ListItemText>
					</MenuItem>
				)}
				{showResetChat && (
					<MenuItem
						onClick={() => {
							setMenuAnchorEl(null);
							setResetError('');
							setResetDialogOpen(true);
						}}
						sx={{ color: 'error.main' }}
					>
						<ListItemIcon sx={{ color: 'error.main' }}><DeleteOutlineIcon fontSize="small" /></ListItemIcon>
						<ListItemText>{labels.deleteChat}</ListItemText>
					</MenuItem>
				)}
				<Divider />
				<MenuItem disabled sx={{ opacity: '1 !important', minHeight: '34px !important' }}>
					<ListItemIcon><InfoOutlinedIcon fontSize="small" /></ListItemIcon>
					<ListItemText
						primary={labels.version}
						secondary={widgetVersion ? `v${widgetVersion}` : '—'}
					/>
				</MenuItem>
			</Menu>
			<Menu
				id="qee-chat-files-menu"
				anchorEl={filesMenuAnchorEl}
				open={Boolean(filesMenuAnchorEl)}
				onClose={() => setFilesMenuAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				MenuListProps={{ dense: true, sx: { py: 0.5 } }}
				PaperProps={{
					sx: {
						mt: 0.5,
						width: 340,
						maxWidth: 'calc(100vw - 16px)',
						maxHeight: 'min(52vh, 430px)',
						borderRadius: 2,
						border: '1px solid',
						borderColor: '#b7d7c5',
						bgcolor: 'background.paper',
						boxShadow: '0 8px 28px rgba(0,0,0,0.14)',
						overflowY: 'auto',
						'& .MuiMenuItem-root': {
							minHeight: 48,
							px: 1.25,
							py: 0.65,
							mx: 0.5,
							borderRadius: 1.25,
							textTransform: 'none !important',
							'&:hover': { bgcolor: 'action.hover' }
						},
						'& .MuiListItemIcon-root': { minWidth: 34, color: 'var(--primary-color, #004c3f)' },
						'& .MuiListItemText-primary': {
							fontSize: '1rem', lineHeight: 1.25, fontWeight: 500,
							whiteSpace: 'normal', overflowWrap: 'anywhere', textTransform: 'none !important'
						},
						'& .MuiListItemText-secondary': {
							fontSize: '0.78rem', lineHeight: 1.25,
							whiteSpace: 'normal', overflowWrap: 'anywhere'
						},
						'@media (max-width:599.95px) and (hover:none) and (pointer:coarse)': {
							width: 330,
							maxHeight: '55vh',
							'& .MuiListItemText-primary': { fontSize: '1.25rem' },
							'& .MuiListItemText-secondary': { fontSize: '1rem' }
						}
					}
				}}
			>
				<MenuItem disabled sx={{ opacity: '1 !important', minHeight: '40px !important' }}>
					<ListItemIcon><FolderOutlinedIcon fontSize="small" /></ListItemIcon>
					<ListItemText primary={labels.savedFiles} />
				</MenuItem>
				<Divider />
				{guruFilesLoading && (
					<MenuItem disabled sx={{ opacity: '1 !important', justifyContent: 'center' }}>
						<CircularProgress size={20} sx={{ mr: 1.25, color: 'var(--primary-color, #004c3f)' }} />
						<ListItemText primary={labels.loadingFiles} />
					</MenuItem>
				)}
				{!guruFilesLoading && guruFilesError && (
					<MenuItem disabled sx={{ opacity: '1 !important', color: 'error.main' }}>
						<ListItemText primary={guruFilesError} />
					</MenuItem>
				)}
				{!guruFilesLoading && !guruFilesError && guruFiles.length === 0 && (
					<MenuItem disabled sx={{ opacity: '1 !important' }}>
						<ListItemText primary={labels.noFiles} />
					</MenuItem>
				)}
				{!guruFilesLoading && !guruFilesError && guruFiles.map((item) => (
					<MenuItem key={`${item.fileType}-${item.sdocUnid || item.fileId || item.fileName}`} onClick={() => openGuruFile(item)}>
						<ListItemIcon>{item.fileType === 'excel'
							? <GridOnOutlinedIcon fontSize="small" />
							: <PictureAsPdfOutlinedIcon fontSize="small" />}</ListItemIcon>
						<ListItemText
							primary={item.fileName || item.title || labels.savedFiles}
							secondary={guruPdfFilenameHasTimestamp(item.fileName)
								? ''
								: formatGuruPdfDate(item.createdAt)}
						/>
					</MenuItem>
				))}
			</Menu>
			<Box
				sx={{
					flex: '1 1 auto',
					minHeight: 0,
					overflowY: 'auto',
					WebkitOverflowScrolling: 'touch',
					display: 'flex',
					flexDirection: 'column',
					'& > *': {
						maxWidth: '100%'
					},
					px: '5px',
					pt: 0.5,
					pb: 1,
					mb: 0
				}}
				ref={msgRef}
			>
			
			<div style={{ width: '100%' }}>
			{messages.map((msg, index) => {
				let showDate = false;
				let showTime = !!chatCtl.getOption().showDateTime;

				if (!!chatCtl.getOption().showDateTime && !msg.deletedAt) {
					const current = dayjs(msg.updatedAt ? msg.updatedAt : msg.createdAt);

					if (current.format('YYYYMMDD') !== prevDate.format('YYYYMMDD')) {
						showDate = true;
					}

					prevDate = current;

					if (current.diff(prevTime) < 60000) {
						showTime = false;
					} else {
						prevTime = current;
					}
				}

				return (
					<React.Fragment key={index}>
						{msg.type === 'text' || msg.type === 'jsx' ? (
							<MuiMessage
								chatController={chatCtl}
								id={`cu-msg-${index + 1}`}
								message={msg}
								showDate={showDate}
								showTime={showTime}
								showSupportActions={index === latestAssistantIndex}
								onSupportRequest={onSupportRequest}
								onGuruPdfDownload={onDownloadGuruPdf}
								onGuruExcelDownload={onDownloadGuruExcel}
								supportLanguage={supportLanguage}
								supportUserEmail={supportUserEmail}
								forceSupportCard={humanSupportRequest.message === msg ? humanSupportRequest.id : 0}
								forceSupportNeedsEmail={supportNeedsEmail}
								messageIndex={index}
								messages={messages}
								enableLeylaBoss={enableLeylaBoss}
								leylaBossCooldown={leylaBossCooldown}
								assistantAvatarLabels={assistantAvatarLabels}
								chatUiLabels={chatUiLabels}
							/>
						) : msg.type === 'file' ? (
							<MuiMessage
								chatController={chatCtl}							
								id={`cu-msg-${index + 1}`}
								message={{
									...msg,
									content: ''
								}}
								showDate={showDate}
								showTime={showTime}
							/>
						) : (
							<MuiMessage
								chatController={chatCtl}							
								id={`cu-msg-${index + 1}`}
								message={unknownMsg}
								showDate={showDate}
								showTime={showTime}
							/>
						)}					
						{index === messages.length - 1 && (
							<Box key="blind-message" sx={{ height: '20px' }}></Box>
						)}
					</React.Fragment>
				);
			})}
			{pendingStatus && (
				<Box
					role="status"
					aria-live="polite"
					sx={{
						my: { xs: 0.5, sm: 1 },
						px: { xs: 1, sm: 1.5 },
						py: 0.75,
						maxWidth: 'calc(100% - 8px)',
						color: 'text.secondary'
					}}
				>
					<Typography variant="body2" sx={{ fontStyle: 'italic', overflowWrap: 'anywhere' }}>
						{pendingStatus}
					</Typography>
				</Box>
			)}
			</div>
			</Box>

			<Box
					sx={{
					position: 'relative',
					flex: '0 0 auto',
					flexShrink: 0,
					bgcolor: 'background.default',
					pb: 'calc(6px + env(safe-area-inset-bottom, 0px))',
					width: '100%',
					zIndex: 10,
				}}
			>
				{pendingStatus ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 52, px: '5px' }}>
						<Box
							component="button"
							type="button"
							disabled
							aria-label={pendingStatus}
							sx={{
								width: 58,
								height: 40,
								p: 0,
								border: '1px solid',
								borderColor: 'divider',
								borderRadius: '22px',
								bgcolor: 'background.paper',
								boxShadow: 2,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								gap: 0.55
							}}
						>
							{[0, 1, 2].map((dot) => (
								<Box
									key={dot}
									sx={{
										width: 6,
										height: 6,
										borderRadius: '50%',
										backgroundColor: 'var(--primary-color, #004c3f)',
										animation: 'qeePendingPulse 1.2s ease-in-out infinite',
										animationDelay: `${dot * 0.18}s`,
										'@keyframes qeePendingPulse': {
											'0%, 60%, 100%': { opacity: 0.3, transform: 'translateY(0)' },
											'30%': { opacity: 1, transform: 'translateY(-3px)' }
										}
									}}
								/>
							))}
						</Box>
					</Box>
				) : (
					<>
						{showQuickQuestions && actReqQuickQuestions && (
							<MuiSelectInput chatController={chatCtl} actionRequest={actReqQuickQuestions} isQuickQuestion={true} />
						)}
						{actReq && actReq.type === 'select' && (
							<MuiSelectInput
								chatController={chatCtl}
								actionRequest={actReq}
								isQuickQuestion={Boolean(actReq.isInitialQuestions)}
							/>
						)}
						{actReq && actReq.type === 'multi-select' && <MuiMultiSelectInput chatController={chatCtl} actionRequest={actReq} />}
						{actReq && actReq.type === 'file' && <MuiFileInput chatController={chatCtl} actionRequest={actReq} />}
						{actReq && actReq.type === 'audio' && <MuiAudioInput chatController={chatCtl} actionRequest={actReq} />}
						{actReq && actReq.type === 'custom' && <CustomComponent chatController={chatCtl} actionRequest={actReq} />}
						{actReq && actReq.type === 'text' && <MuiTextInput chatController={chatCtl} actionRequest={actReq} sendButtonText={labels.send} placeholder={labels.messagePlaceholder} />}
					</>
				)}
			</Box>
			<Dialog
				open={resetDialogOpen}
				onClose={() => !isResettingChat && setResetDialogOpen(false)}
				aria-labelledby="reset-chat-dialog-title"
				PaperProps={{
					sx: {
						borderRadius: 2,
						minWidth: { xs: 'calc(100% - 32px)', sm: 420 },
						boxShadow: 8
					}
				}}
			>
				<DialogTitle id="reset-chat-dialog-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
					<DeleteOutlineIcon color="primary" />
					{labels.resetTitle}
				</DialogTitle>
				<DialogContent>
					<DialogContentText>{labels.resetText}</DialogContentText>
					{resetError && (
						<DialogContentText color="error" sx={{ mt: 1 }}>
							{resetError}
						</DialogContentText>
					)}
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2.5 }}>
					<QeeColorButtonSecondary
						disabled={isResettingChat}
						onClick={() => setResetDialogOpen(false)}
						sx={{ bgcolor: 'background.paper' }}
					>
						{labels.cancel}
					</QeeColorButtonSecondary>
					<QeeColorButtonPrimary disabled={isResettingChat} onClick={confirmResetChat}>
						{labels.confirmDelete}
					</QeeColorButtonPrimary>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
